import { Link } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { Mail, MapPin, Phone, ShieldCheck, Waves, ExternalLink } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const OFFICE = {
    address: "Suite 8, Jagdish Gosai Complex, Nadi, Fiji",
    phone: "+679 714 2741",
    email: "info@bularealestatefiji.com",
};

// Google Maps embed for the Nadi office (no API key required for the basic embed iframe)
const MAP_EMBED_URL =
    "https://www.google.com/maps?q=Jagdish+Gosai+Complex,+Nadi,+Fiji&output=embed";
const MAP_LINK = "https://www.google.com/maps/search/?api=1&query=Jagdish+Gosai+Complex,+Nadi,+Fiji";

export default function Footer() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    const subscribe = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/newsletter", { email });
            toast.success("Vinaka! You're on the list.");
            setEmail("");
        } catch (err) {
            toast.error("Could not subscribe. Try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <footer className="bg-ocean-deep-grad text-white mt-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-4">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="bg-white p-1.5 rounded-lg shadow-sm">
                                <img src="/brand/logo_icon_512.png" alt="Bula Real Estate Fiji" className="h-10 w-10 object-contain" />
                            </div>
                            <div>
                                <div className="font-display text-xl font-bold">Bula Real Estate Fiji</div>
                                <div className="text-[10px] tracking-[0.3em] uppercase text-turquoise">The Pacific Joint Venture Marketplace</div>
                            </div>
                        </div>
                        <p className="text-white/80 max-w-md font-app text-sm leading-relaxed">
                            The premier real estate investment & joint venture ecosystem for Fiji and all Fijian islands.
                            Connecting landowners with capital — across Viti Levu, Vanua Levu, the Mamanucas, the Yasawas, and beyond.
                        </p>
                        <div className="mt-6 flex flex-wrap gap-4 text-xs text-white/70">
                            <div className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-turquoise" /> Verified Intermediary</div>
                            <div className="flex items-center gap-1.5"><Waves className="h-3.5 w-3.5 text-turquoise" /> Pacific-First</div>
                        </div>
                    </div>

                    <div className="lg:col-span-3" data-testid="footer-office-address">
                        <h4 className="font-app font-semibold uppercase tracking-wider text-xs text-turquoise mb-4">Visit Our Office</h4>
                        <div className="space-y-3 font-app text-sm text-white/85">
                            <div className="flex gap-2">
                                <MapPin className="h-4 w-4 text-turquoise flex-shrink-0 mt-0.5" />
                                <a href={MAP_LINK} target="_blank" rel="noopener noreferrer" className="hover:text-turquoise leading-relaxed">
                                    {OFFICE.address}
                                </a>
                            </div>
                            <div className="flex gap-2">
                                <Phone className="h-4 w-4 text-turquoise flex-shrink-0 mt-0.5" />
                                <a href={`tel:${OFFICE.phone.replace(/\s/g, "")}`} className="hover:text-turquoise" data-testid="footer-phone">{OFFICE.phone}</a>
                            </div>
                            <div className="flex gap-2">
                                <Mail className="h-4 w-4 text-turquoise flex-shrink-0 mt-0.5" />
                                <a href={`mailto:${OFFICE.email}`} className="hover:text-turquoise break-all" data-testid="footer-email">{OFFICE.email}</a>
                            </div>
                            <a href={MAP_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-turquoise hover:underline mt-2">
                                Open in Google Maps <ExternalLink className="h-3 w-3" />
                            </a>
                        </div>
                    </div>

                    <div className="lg:col-span-3">
                        <h4 className="font-app font-semibold uppercase tracking-wider text-xs text-turquoise mb-4">Find Us</h4>
                        <div className="rounded-lg overflow-hidden border border-white/15 shadow-lg">
                            <iframe
                                title="Bula Real Estate Fiji office map"
                                src={MAP_EMBED_URL}
                                width="100%"
                                height="180"
                                style={{ border: 0, filter: "saturate(0.85)" }}
                                allowFullScreen=""
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                data-testid="footer-google-map"
                            />
                        </div>
                    </div>

                    <div className="lg:col-span-2">
                        <h4 className="font-app font-semibold uppercase tracking-wider text-xs text-turquoise mb-4">Newsletter</h4>
                        <p className="text-xs text-white/70 font-app mb-3">Exclusive Fijian land deals & Joint Ventures.</p>
                        <form onSubmit={subscribe} className="flex flex-col gap-2">
                            <Input
                                data-testid="newsletter-email-input"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@email.com"
                                className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                            />
                            <Button data-testid="newsletter-subscribe-btn" disabled={loading} className="bg-coral hover:bg-coral-deep text-white font-app">
                                {loading ? "..." : "Join"}
                            </Button>
                        </form>
                        <div className="mt-6 flex flex-col gap-1.5 font-app text-xs text-white/70">
                            <Link to="/marketplace" className="hover:text-turquoise">Marketplace</Link>
                            <Link to="/jv" className="hover:text-turquoise">Joint Venture Portal</Link>
                            <Link to="/hall-of-fame" className="hover:text-turquoise">Hall of Fame</Link>
                            <Link to="/how-it-works" className="hover:text-turquoise">How Joint Venture Works</Link>
                            <Link to="/roi-calculator" className="hover:text-turquoise">ROI Calculator</Link>
                            <Link to="/tltb-partnership" data-testid="footer-tltb-link" className="hover:text-turquoise">TLTB Partnership Pack</Link>
                        </div>
                    </div>
                </div>

                <div className="mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-white/60 font-app">
                    <div>© {new Date().getFullYear()} Bula Real Estate Fiji — All rights reserved.</div>
                    <div className="flex gap-4">
                        <span>Privacy</span>
                        <span>Terms</span>
                        <span>Compliance</span>
                    </div>
                </div>
            </div>
        </footer>
    );
}
